import React from "react";
import {
  CheckCircle2,
  Building2,
  Calendar,
  Clock,
  MapPin,
  Globe,
  Navigation,
  Image as ImageIcon,
  FileText,
  Users,
  Gift,
  Ticket,
  DollarSign,
  ArrowLeft,
  Send,
} from "lucide-react";
import { toast } from "react-toastify";
import { formatPrice, getTicketCurrency } from "../../../../../utils/currency";

/**
 * Step Four: Review Event Details Component
 * Beautiful review page showing all event information before submission
 */
const StepFourReviewEvent = ({ formData, onBack, onSubmit }) => {
  const handleSubmit = () => {
    // Final validation
    if (!formData.organizer) {
      toast.error("Please select an organizer");
      return;
    }
    if (!formData.eventType || formData.eventType.length === 0) {
      toast.error("Please select at least one event type");
      return;
    }
    if (!formData.thumbnail) {
      toast.error("Please upload a thumbnail image");
      return;
    }

    onSubmit(formData);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not specified";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return "Not specified";
    try {
      const [hours, minutes] = timeString.split(":");
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? "PM" : "AM";
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    } catch {
      return timeString;
    }
  };

  const EVENT_TYPES = {
    concerts: "🎵 Concerts",
    drama: "🎭 Drama",
    festival: "🎪 Festival",
    sports: "⚽ Sports",
    theatre: "🎬 Theatre",
    performing_arts: "🎨 Performing Arts",
    career_fairs: "💼 Career Fairs",
    meetups: "🤝 Meetups",
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm sm:rounded-2xl">
        <div className="flex items-center space-x-3 mb-2">
          <div className="p-2 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg shadow">
            <CheckCircle2 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">
              Review Event Details
            </h2>
            <p className="mt-0.5 text-xs text-gray-600 sm:text-sm">
              Review all information before submitting your event
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="space-y-6 lg:col-span-2">
          {/* Organizer Section */}
          {formData.organizer && (
            <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
              <div className="flex items-center mb-4 space-x-2">
                <Building2 className="w-5 h-5 text-blue-600" />
                <h3 className="text-lg font-bold text-gray-900">Organizer</h3>
              </div>
              <div className="flex items-center space-x-4">
                {formData.organizer.logo ? (
                  <img
                    src={formData.organizer.logo}
                    alt={formData.organizer.name}
                    className="w-16 h-16 rounded-xl object-cover border-2 border-gray-200"
                  />
                ) : (
                  <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl">
                    <Building2 className="w-8 h-8 text-white" />
                  </div>
                )}
                <div>
                  <p className="text-base font-bold text-gray-900">
                    {formData.organizer.name}
                  </p>
                  <p className="text-xs text-gray-600">{formData.organizer.email}</p>
                  <p className="text-xs text-gray-600">
                    {formData.organizer.city}, {formData.organizer.country}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Event Information Section */}
          <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
            <div className="flex items-center mb-4 space-x-2">
              <Calendar className="w-5 h-5 text-purple-600" />
              <h3 className="text-lg font-bold text-gray-900">Event Information</h3>
            </div>
            <div className="space-y-4">
              {/* Event Title */}
              {formData.eventName && (
                <div>
                  <p className="mb-2 text-xs font-semibold text-gray-700">
                    Event Title:
                  </p>
                  <p className="text-base font-bold text-gray-900">
                    {formData.eventName}
                  </p>
                </div>
              )}

              {/* Event Types */}
              {formData.eventType && formData.eventType.length > 0 && (
                <div>
                  <p className="mb-2 text-xs font-semibold text-gray-700">
                    Event Type:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {formData.eventType.map((type, index) => (
                      <span
                        key={index}
                        className="px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-100 rounded-lg"
                      >
                        {EVENT_TYPES[type] || type}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Date and Time */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-xs font-semibold text-gray-700">Start Date</p>
                    <p className="text-sm text-gray-900">
                      {formatDate(formData.eventStartDate || formData.eventDate)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-xs font-semibold text-gray-700">End Date</p>
                    <p className="text-sm text-gray-900">
                      {formatDate(formData.eventEndDate || formData.eventStartDate || formData.eventDate)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-xs font-semibold text-gray-700">Start Time</p>
                    <p className="text-sm text-gray-900">
                      {formatTime(formData.eventStartTime || formData.eventTime)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-xs font-semibold text-gray-700">End Time</p>
                    <p className="text-sm text-gray-900">
                      {formatTime(formData.eventEndTime || formData.eventStartTime || formData.eventTime)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Venue Information */}
              <div className="pt-4 border-t border-gray-200">
                <p className="mb-3 text-xs font-semibold text-gray-700">Venue Details</p>
                <div className="space-y-2">
                  {formData.venueName && (
                    <div className="flex items-start space-x-2">
                      <Building2 className="w-4 h-4 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-600">Venue Name</p>
                        <p className="text-sm font-medium text-gray-900">
                          {formData.venueName}
                        </p>
                      </div>
                    </div>
                  )}
                  {formData.venueStreetAddress && (
                    <div className="flex items-start space-x-2">
                      <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-600">Street Address</p>
                        <p className="text-sm font-medium text-gray-900">
                          {formData.venueStreetAddress}
                        </p>
                      </div>
                    </div>
                  )}
                  {(formData.city || formData.country) && (
                    <div className="flex items-start space-x-2">
                      <Globe className="w-4 h-4 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-600">Location</p>
                        <p className="text-sm font-medium text-gray-900">
                          {formData.city}
                          {formData.city && formData.country && ", "}
                          {formData.country}
                        </p>
                      </div>
                    </div>
                  )}
                  {formData.venueGoogleLocation && (
                    <div className="flex items-start space-x-2">
                      <Navigation className="w-4 h-4 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-600">Google Maps</p>
                        <a
                          href={formData.venueGoogleLocation}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
                        >
                          View Location
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Media Section */}
          {(formData.thumbnail || (formData.featuredImages && formData.featuredImages.length > 0)) && (
            <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
              <div className="flex items-center mb-4 space-x-2">
                <ImageIcon className="w-5 h-5 text-purple-600" />
                <h3 className="text-lg font-bold text-gray-900">Media</h3>
              </div>
              <div className="space-y-4">
                {formData.thumbnail && (
                  <div>
                    <p className="mb-2 text-xs font-semibold text-gray-700">
                      Thumbnail:
                    </p>
                    <div className="overflow-hidden rounded-lg border-2 border-gray-200 aspect-video max-w-md">
                      <img
                        src={formData.thumbnail}
                        alt="Event thumbnail"
                        className="object-cover w-full h-full"
                      />
                    </div>
                  </div>
                )}
                {formData.featuredImages && formData.featuredImages.length > 0 && (
                  <div>
                    <p className="mb-2 text-xs font-semibold text-gray-700">
                      Featured Images ({formData.featuredImages.length}):
                    </p>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                      {formData.featuredImages.map((image, index) => (
                        <div
                          key={index}
                          className="overflow-hidden rounded-lg border-2 border-gray-200 aspect-video"
                        >
                          <img
                            src={image}
                            alt={`Featured ${index + 1}`}
                            className="object-cover w-full h-full"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* About Event */}
          {formData.aboutEvent && (
            <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
              <div className="flex items-center mb-4 space-x-2">
                <FileText className="w-5 h-5 text-purple-600" />
                <h3 className="text-lg font-bold text-gray-900">About This Event</h3>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                {formData.aboutEvent}
              </p>
            </div>
          )}

          {/* Performers */}
          {formData.performers && formData.performers.length > 0 && (
            <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
              <div className="flex items-center mb-4 space-x-2">
                <Users className="w-5 h-5 text-purple-600" />
                <h3 className="text-lg font-bold text-gray-900">
                  Performers / Entertainers
                </h3>
                <span className="px-2 py-0.5 text-xs font-semibold text-purple-700 bg-purple-100 rounded-full">
                  {formData.performers.length}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.performers.map((performer, index) => {
                  const name = typeof performer === "string" ? performer : performer?.name;
                  const image = typeof performer === "object" && performer?.image ? performer.image : null;
                  return (
                    <div
                      key={index}
                      className="flex items-center space-x-2 px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg"
                    >
                      {image ? (
                        <img src={image} alt={name} className="w-8 h-8 rounded-full object-cover" />
                      ) : (
                        <Users className="w-4 h-4 text-purple-600" />
                      )}
                      <span className="text-sm font-medium text-gray-900">{name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Sponsors */}
          {formData.sponsors && formData.sponsors.length > 0 && (
            <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
              <div className="flex items-center mb-4 space-x-2">
                <Building2 className="w-5 h-5 text-purple-600" />
                <h3 className="text-lg font-bold text-gray-900">Sponsors</h3>
                <span className="px-2 py-0.5 text-xs font-semibold text-purple-700 bg-purple-100 rounded-full">
                  {formData.sponsors.length}
                </span>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {formData.sponsors.map((sponsor, index) => (
                  <div
                    key={index}
                    className="flex items-center space-x-3 p-3 bg-purple-50 border border-purple-200 rounded-lg"
                  >
                    {sponsor.logo ? (
                      <img
                        src={sponsor.logo}
                        alt={sponsor.name}
                        className="w-12 h-12 rounded-lg object-contain border border-purple-300"
                      />
                    ) : (
                      <div className="flex items-center justify-center w-12 h-12 bg-white rounded-lg border border-purple-300">
                        <Building2 className="w-6 h-6 text-purple-400" />
                      </div>
                    )}
                    <span className="text-sm font-semibold text-gray-900">
                      {sponsor.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Event Schedule */}
          {formData.agenda && formData.agenda.length > 0 && (
            <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
              <div className="flex items-center mb-4 space-x-2">
                <Calendar className="w-5 h-5 text-purple-600" />
                <h3 className="text-lg font-bold text-gray-900">Event Schedule</h3>
                <span className="px-2 py-0.5 text-xs font-semibold text-purple-700 bg-purple-100 rounded-full">
                  {formData.agenda.length}
                </span>
              </div>
              <div className="space-y-3">
                {formData.agenda.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-start space-x-3 p-3 bg-purple-50 border border-purple-200 rounded-lg"
                  >
                    <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 bg-purple-600 rounded-full">
                      <span className="text-xs font-bold text-white">{index + 1}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <h4 className="text-sm font-bold text-gray-900">
                          {item.title}
                        </h4>
                        <div className="flex items-center space-x-1 text-xs text-gray-600">
                          <Clock className="w-3 h-3" />
                          <span>{formatTime(item.time)}</span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-700 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar - Tickets Summary */}
        <div className="lg:col-span-1">
          <div className="sticky top-6 space-y-6">
            {/* Tickets Summary Card */}
            {formData.overallSlots && (
              <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-xl border-2 border-green-300 shadow-lg">
                <div className="flex items-center mb-4 space-x-2">
                  <Ticket className="w-5 h-5 text-green-700" />
                  <h3 className="text-lg font-bold text-gray-900">Tickets Summary</h3>
                </div>
                <div className="space-y-3">
                  <div className="p-3 bg-white rounded-lg border border-green-200">
                    <p className="text-xs font-semibold text-gray-700 mb-1">
                      Overall Slots
                    </p>
                    <p className="text-2xl font-bold text-green-700">
                      {formData.overallSlots}
                    </p>
                  </div>
                  {formData.ticketTypes && formData.ticketTypes.length > 0 && (
                    <div>
                      <p className="mb-2 text-xs font-semibold text-gray-700">
                        Ticket Types ({formData.ticketTypes.length}):
                      </p>
                      <div className="space-y-2">
                        {formData.ticketTypes.map((ticket, index) => (
                          <div
                            key={index}
                            className="p-3 bg-white rounded-lg border border-green-200"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-sm font-bold text-gray-900">
                                {ticket.name}
                              </h4>
                              <span className="px-2 py-0.5 text-xs font-semibold text-green-700 bg-green-100 rounded">
                                {ticket.slotsAllocated} slots
                              </span>
                            </div>
                            <div className="flex items-center space-x-1 mb-2">
                              <DollarSign className="w-3.5 h-3.5 text-green-600" />
                              <span className="text-sm font-semibold text-green-700">
                                {formatPrice(ticket.price, getTicketCurrency(ticket))}
                              </span>
                            </div>
                            {ticket.whatsIncluded &&
                              Array.isArray(ticket.whatsIncluded) &&
                              ticket.whatsIncluded.length > 0 && (
                                <div className="pt-2 border-t border-green-200">
                                  <p className="mb-1 text-xs font-semibold text-gray-700">
                                    Includes:
                                  </p>
                                  <ul className="space-y-1">
                                    {ticket.whatsIncluded.map((item, itemIndex) => (
                                      <li
                                        key={itemIndex}
                                        className="flex items-start space-x-1.5 text-xs text-gray-700"
                                      >
                                        <Gift className="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" />
                                        <span>{item}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Quick Stats */}
            <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
              <h3 className="mb-3 text-sm font-bold text-gray-900">Quick Stats</h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-600">Event Types</span>
                  <span className="text-xs font-semibold text-gray-900">
                    {formData.eventType?.length || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-600">Featured Images</span>
                  <span className="text-xs font-semibold text-gray-900">
                    {formData.featuredImages?.length || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-600">Performers</span>
                  <span className="text-xs font-semibold text-gray-900">
                    {formData.performers?.length || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-600">Sponsors</span>
                  <span className="text-xs font-semibold text-gray-900">
                    {formData.sponsors?.length || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-600">Agenda Items</span>
                  <span className="text-xs font-semibold text-gray-900">
                    {formData.agenda?.length || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-600">Ticket Types</span>
                  <span className="text-xs font-semibold text-gray-900">
                    {formData.ticketTypes?.length || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex flex-col gap-3 pt-4 border-t border-gray-200 sm:flex-row">
        <button
          onClick={onBack}
          className="flex-1 px-6 py-3 text-sm font-semibold text-gray-700 bg-white border-2 border-gray-200 hover:bg-gray-50 rounded-lg transition-all flex items-center justify-center space-x-2"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back</span>
        </button>
        <button
          onClick={handleSubmit}
          className="flex-1 px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center justify-center space-x-2"
        >
          <Send className="w-4 h-4" />
          <span>Submit Event</span>
        </button>
      </div>
    </div>
  );
};

export default StepFourReviewEvent;

